/**
 * exit-intent.js
 * Desktop: triggers on cursor moving toward top of viewport (tab exit intent).
 * Mobile: triggers after 40s of inactivity.
 * Shows max once per session (sessionStorage flag).
 * Handles form submit → show discount code.
 */

(function () {
  'use strict';

  const SESSION_KEY = 'fuel_exit_shown';
  const popup = document.getElementById('exit-popup');
  if (!popup) return;

  // ─── Return path after a native Shopify form post ─────────────────────────
  // The customer form posts for real (it has to, for the CAPTCHA token), so the
  // page reloads. Shopify appends ?customer_posted=true on success. Reopen the
  // popup straight into the success state so the shopper still gets their code.
  (function restoreAfterPost() {
    let claiming = false;
    try { claiming = sessionStorage.getItem('fuel_exit_claiming') === '1'; } catch (e) {}
    if (!claiming) return;

    const params = new URLSearchParams(window.location.search);
    const posted = params.get('customer_posted') === 'true' || params.get('contact_posted') === 'true';
    if (!posted) return;

    try { sessionStorage.removeItem('fuel_exit_claiming'); } catch (e) {}

    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('scroll-locked');

    const scratchWrap = popup.querySelector('[data-scratch]');
    const hint = popup.querySelector('[data-scratch-hint]');
    const fw = popup.querySelector('[data-exit-form]');
    const sw = popup.querySelector('[data-exit-success]');
    if (scratchWrap) scratchWrap.hidden = true;
    if (hint) hint.hidden = true;
    if (fw) fw.hidden = true;
    if (sw) sw.hidden = false;

    // Tidy the URL so a refresh doesn't re-trigger this.
    if (window.history && window.history.replaceState) {
      params.delete('customer_posted');
      params.delete('contact_posted');
      const q = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (q ? '?' + q : ''));
    }
  })();

  // Check session — don't show if already seen
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return;
  } catch (e) {}

  let triggered = false;

  function showPopup() {
    if (triggered) return;
    triggered = true;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}

    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('scroll-locked');

    // Paint the scratch foil once the card has laid out.
    requestAnimationFrame(initScratch);

    // Focus the reveal control (email is hidden until the card is scratched).
    const firstFocus = popup.querySelector('[data-scratch-reveal]');
    if (firstFocus) setTimeout(() => { try { firstFocus.focus(); } catch (e) {} }, 450);
  }

  function hidePopup() {
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('scroll-locked');
  }

  // ─── Desktop: mouse-leave trigger ────────────────────────────────────────

  if (!('ontouchstart' in window)) {
    // Wait 3s before activating — don't trigger immediately on page load
    setTimeout(() => {
      document.addEventListener('mouseleave', (e) => {
        if (e.clientY < 10) showPopup(); // Cursor heading to browser chrome/tab bar
      });
    }, 3000);
  }

  // ─── Mobile: inactivity trigger ──────────────────────────────────────────

  if ('ontouchstart' in window) {
    let inactivityTimer = setTimeout(showPopup, 40000); // 40s

    ['touchstart', 'scroll', 'keydown'].forEach((evt) => {
      document.addEventListener(evt, () => {
        clearTimeout(inactivityTimer);
        inactivityTimer = setTimeout(showPopup, 40000);
      }, { passive: true });
    });
  }

  // ─── Close triggers ───────────────────────────────────────────────────────

  popup.addEventListener('click', (e) => {
    if (e.target.closest('[data-exit-popup-close]')) hidePopup();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && popup.classList.contains('is-open')) hidePopup();
  });

  // ─── Form submit ─────────────────────────────────────────────────────────

  const form = popup.querySelector('#exit-intent-form, [data-exit-intent-form]');
  const formWrap = popup.querySelector('[data-exit-form]');
  const successWrap = popup.querySelector('[data-exit-success]');
  const submitBtn = popup.querySelector('.exit-popup__submit');

  if (form) {
    form.addEventListener('submit', async (e) => {
      const emailInput = form.querySelector('input[type="email"]');
      if (!emailInput || !emailInput.validity.valid) {
        e.preventDefault();
        emailInput?.focus();
        return;
      }

      const email = emailInput.value.trim();
      const klaviyoFormId = popup.dataset.klaviyoFormId;

      // Remember that we're mid-claim so the success state can be restored after
      // Shopify's redirect (the native post reloads the page).
      try { sessionStorage.setItem('fuel_exit_claiming', '1'); } catch (err) {}

      if (submitBtn) {
        submitBtn.setAttribute('aria-busy', 'true');
        submitBtn.disabled = true;
      }

      if (klaviyoFormId) {
        // Klaviyo accepts a cross-origin AJAX subscribe and needs no Shopify CAPTCHA,
        // so we can stay on the page here.
        e.preventDefault();
        let ok = false;
        try {
          const res = await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
            method: 'POST',
            headers: { 'content-type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ g: klaviyoFormId, email }),
          });
          ok = res.ok;
        } catch (err) {
          ok = false;
        }

        if (submitBtn) {
          submitBtn.removeAttribute('aria-busy');
          submitBtn.disabled = false;
        }

        if (ok) {
          showSuccess();
        } else {
          showError();
        }
        return;
      }

      // No Klaviyo: submit the real Shopify customer form natively. Do NOT
      // preventDefault — the browser must post the form (with its CAPTCHA token)
      // for Shopify to accept it. The page reloads and we restore the success
      // state below from ?customer_posted=true.
      //
      // Watchdog: if Shopify's invisible CAPTCHA can't produce a token the post
      // never happens and the page just sits there. Don't leave the shopper
      // staring at a dead spinner — surface it and re-enable the button.
      window.setTimeout(() => {
        if (document.visibilityState === 'hidden') return; // navigating away
        if (submitBtn) {
          submitBtn.removeAttribute('aria-busy');
          submitBtn.disabled = false;
        }
        try { sessionStorage.removeItem('fuel_exit_claiming'); } catch (err) {}
        showError();
      }, 9000);
    });
  }

  function showSuccess() {
    if (formWrap) formWrap.hidden = true;
    if (successWrap) successWrap.hidden = false;
  }

  function showError() {
    let err = popup.querySelector('[data-exit-error]');
    if (!err) {
      err = document.createElement('p');
      err.setAttribute('data-exit-error', '');
      err.setAttribute('role', 'alert');
      err.style.cssText = 'margin:8px 0 0;font-size:13px;color:#C81742;';
      (formWrap || popup).appendChild(err);
    }
    err.textContent = "Couldn't save that email — please try again.";
  }

  // ─── Copy code button ─────────────────────────────────────────────────────

  const copyBtn = popup.querySelector('[data-copy-code]');
  const codeEl = popup.querySelector('[data-discount-code]');

  if (copyBtn && codeEl) {
    copyBtn.addEventListener('click', async () => {
      const code = codeEl.textContent.trim();
      try {
        await navigator.clipboard.writeText(code);
        copyBtn.textContent = '✓ Copied!';
        setTimeout(() => { copyBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Copy`; }, 2000);
      } catch (err) {
        // Fallback for older browsers
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        copyBtn.textContent = '✓ Copied!';
      }
    });
  }

  // ─── Scratch card ──────────────────────────────────────────────────────────

  const scratch = popup.querySelector('[data-scratch]');
  const canvas = popup.querySelector('[data-scratch-canvas]');
  const hintEl = popup.querySelector('[data-scratch-hint]');
  const subEl = popup.querySelector('[data-exit-sub]');
  const revealBtn = popup.querySelector('[data-scratch-reveal]');
  let ctx = null, inited = false, revealed = false, drawing = false, lastPt = null, lastCheck = 0;

  function initScratch() {
    if (inited || !canvas || !scratch) return;
    const w = canvas.offsetWidth, h = canvas.offsetHeight;
    if (!w || !h) { setTimeout(initScratch, 120); return; } // wait for layout
    inited = true;

    const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    // Foil: brand-red metallic gradient + diagonal sheen + label.
    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, '#E0244A'); g.addColorStop(0.5, '#c11d40'); g.addColorStop(1, '#8f1233');
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    ctx.globalAlpha = 0.08; ctx.fillStyle = '#fff';
    for (let i = -h; i < w; i += 26) { ctx.save(); ctx.translate(i, 0); ctx.rotate(-0.5); ctx.fillRect(0, -h, 9, h * 3); ctx.restore(); }
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'rgba(255,255,255,0.94)';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.font = '700 15px ui-monospace, Menlo, monospace';
    ctx.fillText('S C R A T C H   H E R E', w / 2, h / 2 - 9);
    ctx.globalAlpha = 0.82; ctx.font = '600 11px ui-monospace, Menlo, monospace';
    ctx.fillText('drag to reveal your reward', w / 2, h / 2 + 13);
    ctx.globalAlpha = 1;

    const pt = (e) => { const r = canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
    const line = (a, b) => {
      const dist = Math.hypot(b.x - a.x, b.y - a.y), steps = Math.max(1, Math.floor(dist / 6));
      ctx.globalCompositeOperation = 'destination-out';
      for (let i = 0; i <= steps; i++) {
        ctx.beginPath();
        ctx.arc(a.x + (b.x - a.x) * i / steps, a.y + (b.y - a.y) * i / steps, 20, 0, 7);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';
      const now = Date.now(); if (now - lastCheck > 220) { lastCheck = now; checkPct(); }
    };
    canvas.addEventListener('pointerdown', (e) => { drawing = true; lastPt = pt(e); line(lastPt, lastPt); canvas.setPointerCapture && canvas.setPointerCapture(e.pointerId); e.preventDefault(); });
    canvas.addEventListener('pointermove', (e) => { if (!drawing) return; const p = pt(e); line(lastPt, p); lastPt = p; e.preventDefault(); });
    window.addEventListener('pointerup', () => { if (!drawing) return; drawing = false; checkPct(); });
  }

  function checkPct() {
    if (!ctx || revealed) return;
    try {
      const d = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let clear = 0, total = 0;
      for (let i = 3; i < d.length; i += 40) { total++; if (d[i] === 0) clear++; }
      if (total && clear / total >= 0.45) revealPrize();
    } catch (e) {}
  }

  function revealPrize() {
    if (revealed) return; revealed = true;
    if (scratch) scratch.classList.add('is-revealed');
    if (hintEl) hintEl.style.display = 'none';
    if (formWrap) formWrap.hidden = false;
    if (subEl) subEl.textContent = "You unlocked 10% off. Drop your email to lock it in.";
    const email = popup.querySelector('input[name="email"]');
    setTimeout(() => { try { email && email.focus({ preventScroll: true }); } catch (e) {} }, 520);
  }

  if (revealBtn) revealBtn.addEventListener('click', revealPrize);
})();
